import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { RpcSelectOption } from '@core-controls/components/rpc-select/models/rpc-select-option';
import { COUNTRIES } from '../../constants/countries.constants';
import { STATES } from '../../constants/states.constants';
import { Address } from '../../interfaces/customer-info/address';
import { AddressTypes } from '../../interfaces/enums/address-types.enum';
import { RpcButtonComponent } from '../../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { RpcSelectComponent } from '../../../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { RpcInputComponent } from '../../../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'addresses-edit-form',
    templateUrl: './addresses-edit-form.component.html',
    styleUrls: ['../profile-info-base/profile-info-base.component.scss', './addresses-edit-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcButtonComponent, RpcSelectComponent, RpcInputComponent, TranslateModule]
})

export class AddressesEditFormComponent implements AfterViewInit {
    @Input()
    public set addresses(addresses: Address[]) {
        this.initializeAddressControls(addresses);
    }

    @Input() parentGroup: UntypedFormGroup;
    @Input() controlName: string;

    public readonly defaultCountryCode = 'US';

    public addressControls: {
        id: number,
        selectTypeOptions: RpcSelectControltOptions,
        line1InputOptions: RpcInputOptions,
        line2InputOptions: RpcInputOptions,
        cityInputOptions: RpcInputOptions,
        selectStateOptions: RpcSelectControltOptions,
        zipCodeInputOptions: RpcInputOptions,
        selectCountryOptions: RpcSelectControltOptions
    }[];

    private readonly addressSelectOptionValues: RpcSelectOption[] = [
        { text: 'PROFILE_COMMON.ENUM_TYPES.OFFICE', value: AddressTypes.office },
        { text: 'PROFILE_COMMON.ENUM_TYPES.WORK', value: AddressTypes.work },
        { text: 'PROFILE_COMMON.ENUM_TYPES.HOME', value: AddressTypes.home },
        { text: 'PROFILE_COMMON.ENUM_TYPES.OTHER', value: AddressTypes.other }
    ];

    private addressArray: UntypedFormArray;
    private tempAddressId = -1;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) { }

    public ngAfterViewInit(): void {
        this.parentGroup.addControl(this.controlName, this.addressArray);
    }

    public onCountryChanged(event: RpcSelectOption, index: number): void {
        if (event.value !== this.defaultCountryCode) {
            this.addressArray.controls[index].get('state').setValue(null);
        }

        this.addressControls[index].selectCountryOptions.selectedValue = event.value;
    }

    public onAdd(): void {
        this.createAddressControl(this.tempAddressId);
        this.tempAddressId -= 1;
        this.changeDetectorRef.detectChanges();
    }

    public onRemove(i: number): void {
        this.addressControls.splice(i, 1);
        this.addressArray.removeAt(i);
    }

    private initializeAddressControls(addresses: Address[]): void {
        this.addressControls = [];
        this.addressArray = this.formBuilder.array([]);

        addresses.forEach(address => this.createAddressControl(address.id, address));
    }

    private createAddressControl(id: number, address: Address = null): void {
        const currentGroup = this.formBuilder.group({ id });

        this.addressControls.push({
            id,
            selectTypeOptions: this.getSelectTypeOptions(currentGroup, address?.type),
            line1InputOptions: this.getLine1InputOptions(currentGroup, address?.line1),
            line2InputOptions: new RpcInputOptions(currentGroup, 'line2', address?.line2),
            cityInputOptions: this.getCityInputOptions(currentGroup, address?.city),
            selectStateOptions: this.getSelectStateOptions(currentGroup, address?.state),
            zipCodeInputOptions: this.getZipCodeInputOptions(currentGroup, address?.zipCode),
            selectCountryOptions: new RpcSelectControltOptions(currentGroup, 'country', COUNTRIES, address?.country ?? this.defaultCountryCode)
        });

        this.addressArray.push(currentGroup);
    }

    private getSelectTypeOptions(group: UntypedFormGroup, selectedType: AddressTypes = null): RpcSelectControltOptions {
        const validatorsMap = [{
            message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.ADDRESS_TYPE_REQUIRED',
            showError: (control: UntypedFormControl) => control.hasError('required'),
            validator: Validators.required
        }];

        return new RpcSelectControltOptions(group, 'type', this.addressSelectOptionValues, selectedType, validatorsMap);
    }

    private getLine1InputOptions(group: UntypedFormGroup, selectedValue: string = null): RpcInputOptions {
        const validatorsMap = [
            {
                message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.ADDRESS_LINE_1_REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('required'),
                validator: Validators.required
            }
        ];

        return new RpcInputOptions(group, 'line1', selectedValue, validatorsMap);
    }

    private getCityInputOptions(group: UntypedFormGroup, selectedValue: string = null): RpcInputOptions {
        const validatorsMap = [
            {
                message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.CITY_REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('required'),
                validator: Validators.required
            }
        ];

        return new RpcInputOptions(group, 'city', selectedValue, validatorsMap);
    }

    private getSelectStateOptions(group: UntypedFormGroup, selectedValue: string = null): RpcSelectControltOptions {
        const validatorsMap = [
            {
                message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.STATE_REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('required'),
                validator: Validators.required
            },
            {
                message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.STATE_LENGTH_REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('minlength'),
                validator: Validators.minLength(2)
            }
        ];

        return new RpcSelectControltOptions(group, 'state', STATES, selectedValue, validatorsMap);
    }

    private getZipCodeInputOptions(group: UntypedFormGroup, selectedValue: string = null): RpcInputOptions {
        const validatorsMap = [
            {
                message: 'ADDRESS_EDIT_FORM.ERRORS.CLIENT.ZIP_CODE_REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('required'),
                validator: Validators.required
            }
        ];

        return new RpcInputOptions(group, 'zipCode', selectedValue, validatorsMap);
    }
}